import router from '@/router';
import { useGnbStore } from '@/stores/gnb';
import { datadogRum } from '@datadog/browser-rum';
import { useAuthStore } from '@/stores/auth';
import { useLocalStorageStore } from '@/stores/localStorage';
import axios from '@/utils/axios';

export type LoginType = 'normal' | 'naver' | 'kakao' | 'sso' | 'external' | 'restored';

const LOGIN_TYPE_STORAGE_KEY = 'dpost-login-type';

/** 세션당 /clients 기반 setUser 1회 — signout·페이지 새로고침 시 리셋 */
let datadogUserSynced = false;
let datadogUserSyncPromise: Promise<void> | null = null;

function resetDatadogUserSyncState() {
  datadogUserSynced = false;
  datadogUserSyncPromise = null;
}

/**
 * Datadog setUser — center는 로그인 시 authStore에 저장된 centerInfo 사용
 */
export const setDatadogUserFromAuth = (clientInfo: { name?: string }, centerInfo: any, loginType: LoginType) => {
  const centerObjectId = centerInfo?._id ?? centerInfo?.centerId ?? 'unknown';
  datadogRum.setUser({
    id: `${centerObjectId}-${clientInfo?.name ?? 'user'}`,
    name: clientInfo?.name ?? '',
    loginType,
    center: centerInfo?.name ?? '',
    centerId: centerInfo?.centerId ?? '',
    centerObjectId,
    // 유인/무인 지점 구분 — stores/auth.ts CenterInfo.staff (true: 유인, false: 무인)
    storeType: centerInfo?.staff ? 'manned' : 'unmanned',
    version: __APP_VERSION__,
  });
};

/**
 * GET /clients 후 Datadog setUser (authStore에는 저장하지 않음).
 * 로그인 success·세션 복원(initializeStore)에서 호출 — 세션당 1회.
 */
export async function syncDatadogUserFromClients() {
  if (datadogUserSynced) return;

  const authStore = useAuthStore();
  if (!authStore.sessionToken) return;

  if (datadogUserSyncPromise) return datadogUserSyncPromise;

  datadogUserSyncPromise = (async () => {
    try {
      const { data } = await axios.get<api.client.getInfo.response>('/clients');
      if (!data.success) return;

      setDatadogUserFromAuth({ name: data.client.name }, authStore.centerInfo, getStoredLoginType());
      datadogUserSynced = true;
    } catch {
      // 로그인/리다이렉트는 막지 않음
    } finally {
      datadogUserSyncPromise = null;
    }
  })();

  return datadogUserSyncPromise;
}

/** 탭 복귀 시 — 이미 setUser 된 세션만 authStore 기준 재적용 */
export function reapplyDatadogUserFromAuthStore() {
  if (!datadogUserSynced) return;

  const authStore = useAuthStore();
  if (!authStore.sessionToken) return;

  setDatadogUserFromAuth(authStore.clientInfo, authStore.centerInfo, getStoredLoginType());
}

export default function useSessionHanlder() {
  const useSignout = () => {
    const authStore = useAuthStore();
    authStore.reset();
    useLocalStorageStore().removeLocalStorage();
    localStorage.removeItem(LOGIN_TYPE_STORAGE_KEY);
    useGnbStore().resetGnbMenu();
    resetDatadogUserSyncState();

    datadogRum.clearUser();
  };

  const success = async ({ sessionToken, clientInfo, centerInfo, tos }: vuex.sessionHandler, loginType: LoginType) => {
    const authStore = useAuthStore();
    authStore.updateSessionToken(sessionToken);
    authStore.updateClientInfo(clientInfo as any);
    authStore.updateCenterInfo(centerInfo as any);
    authStore.updateMarketingAgreement(tos?.marketing ?? null);
    useLocalStorageStore().saveToLocalStorage();
    localStorage.setItem(LOGIN_TYPE_STORAGE_KEY, loginType);

    await syncDatadogUserFromClients();
    await useGnbStore().loadGnbMenu();

    const nextURL = sessionStorage.getItem('redirectURL');
    const redirectURL =
      nextURL === '/outbound/complete' ? useGnbStore().firstOutboundHref : nextURL ?? '/';

    return router.replace(redirectURL);
  };

  const failed = () => {
    useSignout();
    return router.push('/');
  };

  return {
    success,
    failed,
    useSignout,
  };
}

export const getStoredLoginType = (): LoginType => {
  return (localStorage.getItem(LOGIN_TYPE_STORAGE_KEY) as LoginType) ?? 'restored';
};
